<template>
  <div class="accompanist-dashboard">
    <!-- Header -->
    <div class="dashboard-header">
      <div class="header-content">
        <h1 class="page-title">
          <img src="@/assets/flamou/happy.png" alt="Flamou" class="title-icon" />
          Tableau de bord
        </h1>
        <p class="page-subtitle">
          Suivez et valorisez les compétences de vos jeunes accompagnés dans leur parcours professionnel.
        </p>
      </div>

      <!-- User Profile -->
      <div v-if="userProfile" class="user-profile" :class="{ 'expanded': showProfileMenu }" @click.stop="toggleProfileMenu">
        <div class="profile-header">
          <div class="profile-info">
            <div class="profile-name">{{ userProfile.first_name }} {{ userProfile.last_name }}</div>
            <div class="profile-email">{{ userProfile.email }}</div>
          </div>
          <div class="profile-avatar">
            <img v-if="userProfile.has_picture" :src="profilePictureUrl" alt="Photo de profil" class="avatar-img" />
            <div v-else class="avatar-placeholder">
              {{ userProfile.first_name.charAt(0) }}{{ userProfile.last_name.charAt(0) }}
            </div>
          </div>
        </div>

        <!-- Profile Menu Items -->
        <div v-if="showProfileMenu" class="profile-menu-items">
          <div class="profile-menu-item" @click="viewProfile">
            <span class="menu-icon">👤</span>
            Mon profil
          </div>
          <div class="profile-menu-item" @click="logout">
            <span class="menu-icon">🚪</span>
            Se déconnecter
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Chargement de vos données...</p>
    </div>

    <!-- Main Content -->
    <div v-else class="dashboard-content">
      <!-- Stats Overview -->
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-icon-wrapper" style="background: #E0E7FF;">
            <span class="stat-icon" style="color: #4338CA;">👤</span>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ youngUsers.length }}</div>
            <div class="stat-label">Jeunes accompagnés</div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon-wrapper" style="background: #D1FAE5;">
            <span class="stat-icon" style="color: #065F46;">📝</span>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ totalObservations }}</div>
            <div class="stat-label">Observations réalisées</div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon-wrapper" style="background: #FEF3C7;">
            <span class="stat-icon" style="color: #92400E;">🏆</span>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ totalBadges }}</div>
            <div class="stat-label">Badges validés</div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon-wrapper" style="background: #FCE7F3;">
            <span class="stat-icon" style="color: #BE185D;">📈</span>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ averageProgress }}%</div>
            <div class="stat-label">Avancé totale de tous les jeunes</div>
          </div>
        </div>
      </div>

      <!-- Search and Filters -->
      <div class="filters-section">
        <div class="search-box">
          <span class="search-icon">🔍</span>
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Rechercher un jeune..."
            class="search-input"
          />
        </div>

        <div class="filter-buttons">
          <button
            v-for="filter in filters"
            :key="filter.value"
            :class="['filter-btn', { active: selectedFilter === filter.value }]"
            @click="selectedFilter = filter.value"
          >
            {{ filter.label }}
          </button>
        </div>
      </div>

      <!-- Young Users Grid -->
      <div class="young-users-section">
        <h2 class="section-title">
          <span class="title-decoration"></span>
          Mes jeunes accompagnés
        </h2>

        <div v-if="youngUsers.length === 0 && pendingRequests.length === 0" class="empty-state">
          <div class="empty-icon">👥</div>
          <h3>Aucun jeune accompagné</h3>
          <p>Commencez par ajouter un jeune en utilisant son code d'attachement.</p>
          <button class="add-young-btn" @click="showAddYoungModal = true">
            <span class="btn-icon">+</span>
            Ajouter un jeune
          </button>
        </div>

        <div v-else-if="filteredYoungUsers.length === 0 && filteredPendingRequests.length === 0" class="empty-state">
          <div class="empty-icon">🔍</div>
          <h3>Aucun résultat</h3>
          <p>Aucun jeune ne correspond à vos critères de recherche.</p>
        </div>

        <div v-else class="young-users-grid">
          <!-- Young Users Cards -->
          <YoungUserCard
            v-for="youngUser in filteredYoungUsers"
            :key="youngUser.id"
            :young-user="youngUser"
            @click="viewYoungProfile(youngUser)"
            @disconnect="confirmDisconnect(youngUser)"
          />

          <!-- Pending Invitation Cards -->
          <div
            v-for="request in filteredPendingRequests"
            :key="'pending-' + request.request_id"
            class="pending-request-card"
          >
            <div class="pending-badge">En attente</div>
            <div class="pending-avatar">
              {{ request.young_person.first_name.charAt(0) }}
            </div>
            <h3 class="pending-name">
              {{ request.young_person.first_name }}
              <span v-if="request.young_person.last_name">{{ request.young_person.last_name }}</span>
            </h3>
            <p class="pending-status">Invitation envoyée</p>
            <div class="pending-date">le {{ new Date(request.date_requested).toLocaleDateString() }}</div>
          </div>

          <!-- Add Young Card -->
          <div class="add-young-card" @click="showAddYoungModal = true">
            <div class="add-young-icon">+</div>
            <h3 class="add-young-title">Ajouter un jeune</h3>
            <p class="add-young-description">Créer une nouvelle relation d'accompagnement</p>
          </div>
        </div>
      </div>

      <!-- Quick Actions -->
      <div class="quick-actions">
        <h2 class="section-title">
          <span class="title-decoration"></span>
          Actions rapides
        </h2>

        <div class="actions-grid">
          <div class="resource-card" @click="navigateToResources">
            <div class="resource-left">
              <div class="resource-icon">📚</div>
              <div class="resource-info">
                <h3>Ressources & Aide</h3>
                <p>Guides et outils d'observation</p>
              </div>
            </div>
            <div class="resource-arrow">→</div>
          </div>

          <div class="resource-card" style="background: linear-gradient(135deg, #4F46E5 0%, #4F46E5 100%);" @click="showEnterpriseFeedback">
            <div class="resource-left">
              <div class="resource-icon">🏢</div>
              <div class="resource-info">
                <h3>Retours Entreprises</h3>
                <p>Évaluations de stages et commentaires</p>
              </div>
            </div>
            <div class="resource-arrow">→</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Young Modal -->
    <div v-if="showAddYoungModal" class="modal-overlay" @click="closeAddYoungModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>Ajouter un jeune</h2>
          <button class="modal-close" @click="closeAddYoungModal">✕</button>
        </div>
        <div class="modal-body">
          <p class="info-text">
            Saisissez le code d'invitation du jeune que vous souhaitez accompagner.
            Le jeune devra accepter votre demande avant que vous puissiez accéder à son profil.
          </p>

          <div class="form-group">
            <label for="invitationCode" class="form-label">Code d'invitation</label>
            <input
              id="invitationCode"
              type="text"
              v-model="invitationCode"
              placeholder="Ex: JEUNE-ABC123"
              class="form-input"
              :class="{ 'input-error': invitationError }"
              @input="clearError"
              @keyup.enter="sendInvitation"
            />
            <p v-if="invitationError" class="error-message">{{ invitationError }}</p>
          </div>

          <div v-if="invitationSuccess" class="success-message">
            <span class="success-icon">✓</span>
            <p>Invitation envoyée avec succès ! En attente de l'acceptation du jeune.</p>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="closeAddYoungModal">Annuler</button>
          <button
            class="btn-primary"
            @click="sendInvitation"
            :disabled="!invitationCode || invitationLoading"
          >
            <span v-if="invitationLoading" class="btn-spinner"></span>
            {{ invitationLoading ? 'Envoi...' : 'Envoyer l\'invitation' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Logout Confirmation Modal -->
    <div v-if="showLogoutModal" class="modal-overlay" @click="cancelLogout">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>Déconnexion</h2>
          <button class="modal-close" @click="cancelLogout">✕</button>
        </div>
        <div class="modal-body">
          <p class="info-text">
            Êtes-vous sûr de vouloir vous déconnecter ?
          </p>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="cancelLogout">Annuler</button>
          <button class="btn-primary" @click="confirmLogout">
            Se déconnecter
          </button>
        </div>
      </div>
    </div>

    <!-- Disconnect Young Modal -->
    <div v-if="showDisconnectModal" class="modal-overlay" @click="cancelDisconnect">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>Déconnecter un jeune</h2>
          <button class="modal-close" @click="cancelDisconnect">✕</button>
        </div>
        <div class="modal-body">
          <div class="warning-box">
            <span class="warning-icon">⚠️</span>
            <div>
              <p class="warning-title">Attention</p>
              <p class="warning-text">
                Cette action est irréversible et aura les conséquences suivantes :
              </p>
            </div>
          </div>
          <ul class="consequence-list">
            <li>Vous ne pourrez plus accéder au profil de <strong>{{ youngToDisconnect?.firstName }}</strong></li>
            <li>Toutes vos observations seront conservées mais ne seront plus accessibles</li>
            <li>Le jeune devra vous envoyer un nouveau code d'invitation pour rétablir la connexion</li>
          </ul>
          <p class="info-text">
            Êtes-vous sûr de vouloir déconnecter <strong>{{ youngToDisconnect?.firstName }}</strong> ?
          </p>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="cancelDisconnect">Annuler</button>
          <button
            class="btn-danger"
            @click="executeDisconnect"
            :disabled="disconnectLoading"
          >
            <span v-if="disconnectLoading" class="btn-spinner"></span>
            {{ disconnectLoading ? 'Déconnexion...' : 'Déconnecter' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import accompanimentService from '@/services/accompaniment.service';
import YoungUserCard from '@/components/accompaniment/YoungUserCard.vue';
import AuthService from '@/services/AuthService';

export default {
  name: 'AccompanistDashboard',
  components: {
    YoungUserCard
  },
  data() {
    return {
      loading: true,
      youngUsers: [],
      pendingRequests: [],
      userProfile: null,
      showProfileMenu: false,
      searchQuery: '',
      selectedFilter: 'all',
      showAddYoungModal: false,
      showLogoutModal: false,
      showDisconnectModal: false,
      youngToDisconnect: null,
      disconnectLoading: false,
      invitationCode: '',
      invitationLoading: false,
      invitationError: '',
      invitationSuccess: false,
      filters: [
        { label: 'Tous', value: 'all' },
        { label: 'Actifs récemment', value: 'recent' },
        { label: 'Parents', value: 'parent' },
        { label: 'Éducateur', value: 'educateur' }
      ]
    };
  },
  computed: {
    profilePictureUrl() {
      if (this.userProfile && this.userProfile.has_picture) {
        return `${process.env.VUE_APP_API_URL || 'http://localhost:8000'}/user/profile-picture`;
      }
      return '';
    },
    filteredYoungUsers() {
      let filtered = this.youngUsers;

      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(user =>
          user.firstName.toLowerCase().includes(query) ||
          user.lastName.toLowerCase().includes(query) ||
          user.username.toLowerCase().includes(query)
        );
      }

      // Filter by selected filter
      if (this.selectedFilter !== 'all') {
        if (this.selectedFilter === 'recent') {
          const threeDaysAgo = new Date();
          threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
          filtered = filtered.filter(user =>
            new Date(user.lastActivity) >= threeDaysAgo
          );
        } else {
          filtered = filtered.filter(user =>
            user.relationshipType === this.selectedFilter
          );
        }
      }

      return filtered;
    },
    filteredPendingRequests() {
      if (!this.searchQuery) return this.pendingRequests;
      
      const query = this.searchQuery.toLowerCase();
      return this.pendingRequests.filter(request =>
        request.young_person.first_name.toLowerCase().includes(query) ||
        (request.young_person.last_name && request.young_person.last_name.toLowerCase().includes(query)) ||
        request.young_person.username.toLowerCase().includes(query)
      );
    },
    totalObservations() {
      return this.youngUsers.reduce((sum, user) => sum + (user.observationsCount || 0), 0);
    },
    totalBadges() {
      return this.youngUsers.reduce((sum, user) => sum + (user.badgesCount || 0), 0);
    },
    averageProgress() {
      if (this.youngUsers.length === 0) return 0;
      const total = this.youngUsers.reduce((sum, user) => sum + (user.gamesCompletion || 0), 0);
      return Math.round(total / this.youngUsers.length);
    }
  },
  async mounted() {
    await Promise.all([
      this.loadUserProfile(),
      this.loadYoungUsers()
    ]);

    // Fermer le menu profil si on clique ailleurs
    document.addEventListener('click', this.closeProfileMenu);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeProfileMenu);
  },
  methods: {
    async loadUserProfile() {
      try {
        const response = await AuthService.request('get', '/user/profile');
        this.userProfile = response.data;
      } catch (error) {
        console.error('Error loading user profile:', error);
      }
    },
    async loadYoungUsers() {
      try {
        this.loading = true;
        const [usersResponse, pendingResponse] = await Promise.all([
          accompanimentService.getYoungUsers(),
          accompanimentService.getPendingInvitations()
        ]);
        
        this.youngUsers = usersResponse.data;
        this.pendingRequests = pendingResponse.data.pending_requests || [];
      } catch (error) {
        console.error('Error loading young users:', error);
        // TODO: Show error notification
      } finally {
        this.loading = false;
      }
    },
    toggleProfileMenu() {
      this.showProfileMenu = !this.showProfileMenu;
    },
    closeProfileMenu() {
      this.showProfileMenu = false;
    },
    viewProfile() {
      this.showProfileMenu = false;
      this.$router.push({ name: 'AccompanistProfile' });
    },
    logout() {
      this.showProfileMenu = false;
      this.showLogoutModal = true;
    },
    confirmLogout() {
      // Supprimer le token et rediriger vers la page de connexion
      AuthService.logout();
      this.$router.push('/commencer');
    },
    cancelLogout() {
      this.showLogoutModal = false;
    },
    viewYoungProfile(youngUser) {
      this.$router.push({
        name: 'YoungPersonProfile',
        params: { id: youngUser.id }
      });
    },
    navigateToResources() {
      this.$router.push({ name: 'ResourcesPage' });
    },
    showEnterpriseFeedback() {
      alert("Cette fonctionnalité sera bientôt disponible pour permettre aux entreprises de saisir leurs retours.");
    },
    closeAddYoungModal() {
      this.showAddYoungModal = false;
      this.invitationCode = '';
      this.invitationError = '';
      this.invitationSuccess = false;
      this.invitationLoading = false;
    },
    clearError() {
      this.invitationError = '';
    },
    async sendInvitation() {
      if (!this.invitationCode.trim()) {
        this.invitationError = 'Veuillez saisir un code d\'invitation';
        return;
      }

      try {
        this.invitationLoading = true;
        this.invitationError = '';

        // Appel au service (à implémenter côté backend)
        await accompanimentService.sendInvitationByCode(this.invitationCode.trim());

        this.invitationSuccess = true;

        // Fermer le modal après 2 secondes
        setTimeout(() => {
          this.closeAddYoungModal();
          // Recharger la liste des jeunes pour voir les invitations en attente
          this.loadYoungUsers();
        }, 2000);

      } catch (error) {
        console.error('Error sending invitation:', error);

        // Gérer différents types d'erreurs
        if (error.response) {
          switch (error.response.status) {
            case 404:
              this.invitationError = 'Code d\'invitation invalide ou inexistant';
              break;
            case 409:
              this.invitationError = 'Une relation existe déjà avec ce jeune';
              break;
            case 400:
              this.invitationError = 'Format de code invalide';
              break;
            default:
              this.invitationError = 'Une erreur est survenue. Veuillez réessayer.';
          }
        } else {
          this.invitationError = 'Impossible de se connecter au serveur. Vérifiez votre connexion.';
        }
      } finally {
        this.invitationLoading = false;
      }
    },
    confirmDisconnect(youngUser) {
      this.youngToDisconnect = youngUser;
      this.showDisconnectModal = true;
    },
    cancelDisconnect() {
      this.showDisconnectModal = false;
      this.youngToDisconnect = null;
      this.disconnectLoading = false;
    },
    async executeDisconnect() {
      if (!this.youngToDisconnect || !this.youngToDisconnect.connectionId) {
        console.error('No connection ID found for young user');
        this.cancelDisconnect();
        return;
      }

      try {
        this.disconnectLoading = true;

        // Appel au service pour déconnecter
        await accompanimentService.disconnectYoung(this.youngToDisconnect.connectionId);

        // Fermer le modal
        this.cancelDisconnect();

        // Recharger la liste des jeunes
        await this.loadYoungUsers();

        // TODO: Afficher un message de succès à l'utilisateur
        console.log('Jeune déconnecté avec succès');

      } catch (error) {
        console.error('Error disconnecting young user:', error);

        // TODO: Afficher un message d'erreur à l'utilisateur
        alert('Une erreur est survenue lors de la déconnexion. Veuillez réessayer.');
      } finally {
        this.disconnectLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.accompanist-dashboard {
  min-height: 100vh;
  background: linear-gradient(to bottom, #F9FAFB 0%, #FFFFFF 100%);
  padding: 2rem;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 2px solid #E5E7EB;
  position: relative;
  overflow: visible;
}

.header-content {
  flex: 1;
}

/* User Profile Styles */
.user-profile {
  background: white;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 100;
  overflow: hidden;
}

.user-profile:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.user-profile.expanded {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.profile-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1.25rem;
}

.profile-info {
  text-align: right;
}

.profile-name {
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
}

.profile-email {
  font-size: 0.75rem;
  color: #6B7280;
  margin-top: 0.25rem;
}

.profile-avatar {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
}

.avatar-img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #E5E7EB;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.125rem;
  font-weight: 700;
  border: 2px solid #E5E7EB;
}

/* Profile Menu Items */
.profile-menu-items {
  border-top: 1px solid #E5E7EB;
  padding: 0.5rem;
}

.profile-menu-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #374151;
  font-size: 0.9375rem;
  font-weight: 500;
}

.profile-menu-item:hover {
  background: #F3F4F6;
  color: #111827;
}

.profile-menu-item .menu-icon {
  font-size: 1.25rem;
}

.page-title {
  font-size: 2.5rem;
  font-weight: 800;
  color: #111827;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.title-icon {
  width: 3rem;
  height: 3rem;
  object-fit: contain;
}

.page-subtitle {
  font-size: 1.125rem;
  color: #6B7280;
  margin: 0;
  display: flex;
  align-items: left;
}

.add-young-card {
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  border-radius: 16px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.4);
  border: 3px dashed rgba(255, 255, 255, 0.5);
  min-height: 280px;
}

.add-young-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(255, 152, 0, 0.5);
  border-color: rgba(255, 255, 255, 0.8);
}

.add-young-icon {
  font-size: 4rem;
  font-weight: 300;
  color: white;
  margin-bottom: 1rem;
  line-height: 1;
}

.add-young-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: white;
  margin: 0 0 0.5rem 0;
}

.add-young-description {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  color: #6B7280;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #E5E7EB;
  border-top-color: #FF9800;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.dashboard-content {
  max-width: 1400px;
  margin: 0 auto;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.stat-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  transform: translateY(-4px);
}

.stat-icon-wrapper {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-icon {
  font-size: 2rem;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 2rem;
  font-weight: 800;
  color: #111827;
  line-height: 1;
}

.stat-label {
  font-size: 0.875rem;
  color: #6B7280;
  margin-top: 0.25rem;
}

.filters-section {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.search-box {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: #F9FAFB;
  border-radius: 12px;
  margin-bottom: 1rem;
}

.search-icon {
  font-size: 1.25rem;
  color: #9CA3AF;
}

.search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 1rem;
  color: #111827;
  outline: none;
}

.search-input::placeholder {
  color: #9CA3AF;
}

.filter-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.filter-btn {
  background: #F3F4F6;
  color: #6B7280;
  border: 2px solid transparent;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-btn:hover {
  background: #E5E7EB;
}

.filter-btn.active {
  background: #fff5f0;
  color: #FF9800;
  border-color: #FF9800;
}

.young-users-section {
  margin-bottom: 3rem;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.title-decoration {
  width: 4px;
  height: 28px;
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  border-radius: 2px;
}

/* Pending Request Card Styles */
.pending-request-card {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  border: 2px solid #E5E7EB;
  position: relative;
  transition: all 0.3s ease;
  min-height: 280px;
}

.pending-badge {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: #FEF3C7;
  color: #92400E;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  text-transform: uppercase;
}

.pending-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #F3F4F6;
  color: #9CA3AF;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1rem;
  border: 4px solid #F9FAFB;
}

.pending-name {
  font-size: 1.25rem;
  font-weight: 700;
  color: #374151;
  margin: 0 0 0.25rem 0;
}

.pending-status {
  font-size: 0.875rem;
  color: #6B7280;
  margin: 0 0 1rem 0;
}

.pending-date {
  font-size: 0.75rem;
  color: #9CA3AF;
  font-style: italic;
}

.young-users-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
}

.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.empty-state h3 {
  font-size: 1.25rem;
  color: #111827;
  margin: 0 0 0.5rem 0;
}

.empty-state p {
  color: #6B7280;
  margin: 0 0 1.5rem 0;
}

.add-young-btn {
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 0.875rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3);
}

.add-young-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 152, 0, 0.4);
}

.add-young-btn .btn-icon {
  font-size: 1.25rem;
  font-weight: 300;
}

.quick-actions {
  margin-bottom: 2rem;
}

.actions-grid {
  display: flex;
  gap: 1.5rem;
}

.resource-card {
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  border-radius: 16px;
  padding: 1.5rem 2rem;
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  min-height: 100px;
}

.resource-card:hover {
  box-shadow: 0 8px 24px rgba(255, 152, 0, 0.5);
  transform: translateY(-4px);
  border-color: rgba(255, 255, 255, 0.3);
}

.resource-left {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.resource-icon {
  font-size: 3rem;
  flex-shrink: 0;
}

.resource-info {
  text-align: left;
}

.resource-card h3 {
  font-size: 1.25rem;
  font-weight: 700;
  color: white;
  margin: 0 0 0.5rem 0;
}

.resource-card p {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

.resource-arrow {
  font-size: 2rem;
  color: white;
  transition: transform 0.3s ease;
  flex-shrink: 0;
}

.resource-card:hover .resource-arrow {
  transform: translateX(8px);
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: #111827;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #9CA3AF;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.modal-close:hover {
  background: #F3F4F6;
  color: #111827;
}

.modal-body {
  margin-bottom: 1.5rem;
}

.info-text {
  color: #6B7280;
  line-height: 1.6;
  margin-bottom: 1rem;
}

.modal-footer {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

.btn-secondary,
.btn-primary,
.btn-danger {
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-secondary {
  background: #F3F4F6;
  color: #374151;
}

.btn-secondary:hover {
  background: #E5E7EB;
}

.btn-primary {
  background: #FF9800;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #FF9800;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-danger {
  background: #DC2626;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: #B91C1C;
}

.btn-danger:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Form Styles */
.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.form-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #E5E7EB;
  border-radius: 8px;
  font-size: 1rem;
  color: #111827;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #FF9800;
  box-shadow: 0 0 0 3px rgba(255, 152, 0, 0.1);
}

.form-input::placeholder {
  color: #9CA3AF;
}

.form-input.input-error {
  border-color: #EF4444;
}

.form-input.input-error:focus {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.error-message {
  color: #EF4444;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  margin-bottom: 0;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.success-message {
  background: #D1FAE5;
  border: 2px solid #10B981;
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
}

.success-icon {
  background: #10B981;
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  flex-shrink: 0;
}

.success-message p {
  color: #065F46;
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
}

.btn-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  margin-right: 0.5rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.warning-box {
  background: #FEF3C7;
  border: 2px solid #F59E0B;
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.warning-icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.warning-title {
  font-size: 0.875rem;
  font-weight: 700;
  color: #92400E;
  margin: 0 0 0.25rem 0;
}

.warning-text {
  font-size: 0.875rem;
  color: #92400E;
  margin: 0;
  line-height: 1.4;
}

.consequence-list {
  margin: 0 0 1rem 0;
  padding-left: 1.5rem;
}

.consequence-list li {
  color: #6B7280;
  font-size: 0.875rem;
  line-height: 1.6;
  margin-bottom: 0.5rem;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .profile-info {
    display: none;
  }

  .profile-header {
    padding: 0.5rem;
  }

  .user-profile {
    min-width: auto;
    max-width: 64px;
  }

  .profile-menu-items {
    min-width: auto;
    width: 100%;
    padding: 0.25rem;
  }

  .profile-menu-item {
    justify-content: center;
    padding: 0.75rem;
    min-width: auto;
  }

  .profile-menu-item .menu-icon {
    margin: 0;
  }

  /* Hide text in menu items on mobile */
  .profile-menu-item {
    font-size: 0;
  }

  .profile-menu-item .menu-icon {
    font-size: 1.25rem;
  }
}
</style>
